import { PERSONA_REFERENCE_VIDEO_ANALYSIS_PROMPT_USED_IN, PERSONA_SCRIPT_IMPORT_PROMPT_USED_IN } from '../events/persona-opportunities.js';
import {
  CHANGELOG_PROMPT_DEPLOY_NOTES,
  CHANGELOG_PROMPT_ROUNDUP,
} from './changelog-prompts.js';
import type { PromptLlmModelFamily } from './prompt-llm-models.js';
import { promptModelFamily } from './prompt-llm-models.js';
import {
  SCRIPT_REVIEW_PROMPT_ITERATION_TEXT_OVERLAY,
  SCRIPT_REVIEW_PROMPT_ITERATION_VISUAL,
  SCRIPT_REVIEW_PROMPT_ITERATION_VOICEOVER,
  SCRIPT_REVIEW_PROMPT_SCRIPT_EXTRACTION,
  SCRIPT_REVIEW_PROMPT_SCRIPT_MATCH,
  SCRIPT_REVIEW_PROMPT_SUGGESTION,
  SCRIPT_REVIEW_PROMPT_TEXT_OVERLAY,
  SCRIPT_REVIEW_PROMPT_TRANSCRIPT,
  SCRIPT_REVIEW_PROMPT_VISUAL,
  SCRIPT_REVIEW_PROMPT_VOICEOVER,
} from './script-review-prompts.js';

// ─── Which models a call site can actually run ───────────────────────
//
// A prompt's model is only a real choice when the worker behind that call site
// can execute it. The two providers address different Vertex publishers —
// `@nexus/gemini` reaches `publishers/google`, `@nexus/anthropic` reaches
// `publishers/anthropic` — so a Claude id sent down a Gemini-only path returns
// a 404 for a model that publisher does not serve.
//
// Every service already enforced its own expectation in its own way; this map
// is the single declaration those guards and the Prompts model picker read, so
// the dropdown stops offering choices the backend discards.
//
// An empty list means the call site does not honour a per-prompt model at all:
// it fetches the template but takes its model from elsewhere. Those are listed
// explicitly rather than omitted, because "we checked, and it is ignored" and
// "nobody has mapped this yet" are different answers.

const GEMINI_ONLY: readonly PromptLlmModelFamily[] = ['gemini'];
const CLAUDE_ONLY: readonly PromptLlmModelFamily[] = ['claude'];
const EITHER: readonly PromptLlmModelFamily[] = ['gemini', 'claude'];
const MODEL_NOT_USED: readonly PromptLlmModelFamily[] = [];

export const PROMPT_SUPPORTED_FAMILIES: Readonly<
  Record<string, readonly PromptLlmModelFamily[]>
> = {
  // Set generation takes its model from `Flow.llm_model`, not from the prompt.
  'opportunity-generation-premise': MODEL_NOT_USED,
  'opportunity-generation-framework': MODEL_NOT_USED,

  // Script generation and editing run on the Anthropic Messages API.
  'creative-script-generation-angles': CLAUDE_ONLY,
  'creative-script-generation-topics': CLAUDE_ONLY,
  'creative-script-generation-custom': CLAUDE_ONLY,
  'creative-brief-generation-persona-angles': CLAUDE_ONLY,
  'creative-brief-edit-persona-angles': CLAUDE_ONLY,
  'creative-script-hook-variations-persona': CLAUDE_ONLY,
  [PERSONA_SCRIPT_IMPORT_PROMPT_USED_IN]: CLAUDE_ONLY,
  // The edit call reuses the angles/topics model; these two supply text only.
  'creative-script-edit-meta-prompt': MODEL_NOT_USED,
  'creative-script-edit-feedback-message': MODEL_NOT_USED,

  'creative-storyboard-beat-extraction': GEMINI_ONLY,
  'creative-storyboard-image': GEMINI_ONLY,
  'persona-avatar-generation': GEMINI_ONLY,
  [PERSONA_REFERENCE_VIDEO_ANALYSIS_PROMPT_USED_IN]: GEMINI_ONLY,

  [SCRIPT_REVIEW_PROMPT_TRANSCRIPT]: GEMINI_ONLY,
  [SCRIPT_REVIEW_PROMPT_VOICEOVER]: GEMINI_ONLY,
  [SCRIPT_REVIEW_PROMPT_TEXT_OVERLAY]: GEMINI_ONLY,
  [SCRIPT_REVIEW_PROMPT_VISUAL]: GEMINI_ONLY,
  [SCRIPT_REVIEW_PROMPT_SCRIPT_EXTRACTION]: GEMINI_ONLY,
  [SCRIPT_REVIEW_PROMPT_SCRIPT_MATCH]: GEMINI_ONLY,
  [SCRIPT_REVIEW_PROMPT_SUGGESTION]: GEMINI_ONLY,
  [SCRIPT_REVIEW_PROMPT_ITERATION_VOICEOVER]: GEMINI_ONLY,
  [SCRIPT_REVIEW_PROMPT_ITERATION_TEXT_OVERLAY]: GEMINI_ONLY,
  [SCRIPT_REVIEW_PROMPT_ITERATION_VISUAL]: GEMINI_ONLY,

  // The changelog worker routes by family, so both providers are real choices.
  [CHANGELOG_PROMPT_DEPLOY_NOTES]: EITHER,
  [CHANGELOG_PROMPT_ROUNDUP]: EITHER,
};

/**
 * Families a call site can run. An unmapped `usedIn` — a prompt someone added
 * for their own call site — is left permissive, because nothing here knows
 * what runs it.
 */
export function getPromptSupportedFamilies(usedIn: string): readonly PromptLlmModelFamily[] {
  return PROMPT_SUPPORTED_FAMILIES[usedIn] ?? EITHER;
}

/** Whether the call site honours a model chosen on the prompt at all. */
export function promptModelIsHonoured(usedIn: string): boolean {
  return getPromptSupportedFamilies(usedIn).length > 0;
}

/** Whether this model can run at this call site. */
export function isPromptModelSupported(usedIn: string, modelId: string): boolean {
  const family = promptModelFamily(modelId);
  return family !== null && getPromptSupportedFamilies(usedIn).includes(family);
}
